import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";

function Login() {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");

    const result = await login(formData.email, formData.password);

    if (result.success) {
      navigate("/projects");
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleLogin = async () => {
    // Basic implementation - clear previous errors
    setError("");

    const result = await loginWithGoogle();

    if (result && result.success) {
      navigate("/projects");
    } else {
      if (result && result.message) {
        setError(result.message);
      }
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          display: "flex",
          background: "var(--bg-primary)",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "var(--shadow-3xl)",
          maxWidth: "1000px",
          width: "100%",
          minHeight: "600px",
          border: "5px solid var(--bg-primary)",
        }}
      >
        {/* Left Side - Blue Gradient */}
        <div
          style={{
            flex: 1,
            background: "var(--gradient-secondary)",
            padding: "3rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            color: "var(--text-inverse)",
            position: "relative",
            borderRadius: "24px",
          }}
        >
          <div>
            <p
              style={{
                fontSize: "0.9rem",
                opacity: 0.9,
                marginBottom: "1rem",
                fontWeight: "400",
              }}
            >
              {t('auth.youCanEasily')}
            </p>
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                lineHeight: "1.2",
                margin: "0 0 2rem 0",
              }}
            >
              {t('auth.speedUpWork')}
              <br />
              {t('auth.withWebApp')}
            </h1>
          </div>

          {/* Partners Section */}
          <div>
            <p
              style={{
                fontSize: "0.9rem",
                opacity: 0.8,
                marginBottom: "1.5rem",
                fontWeight: "400",
              }}
            >
              {t('auth.ourPartners')}
            </p>
            <div
              style={{
                display: "flex",
                gap: "2rem",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  opacity: 0.7,
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Icon icon="ic:baseline-discord" width="20" height="20" />
                Discord
              </div>
              <div
                style={{
                  opacity: 0.7,
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Icon icon="mdi:instagram" width="20" height="20" />
                Instagram
              </div>
              <div
                style={{
                  opacity: 0.7,
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Icon icon="mdi:spotify" width="20" height="20" />
                Spotify
              </div>
              <div
                style={{
                  opacity: 0.7,
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Icon icon="mdi:youtube" width="20" height="20" />
                YouTube
              </div>
              <div
                style={{
                  opacity: 0.7,
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Icon icon="ic:baseline-tiktok" width="20" height="20" />
                TikTok
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div
          style={{
            flex: 1,
            padding: "3rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            maxWidth: "450px",
          }}
        >
          <div style={{ marginBottom: "2rem" }}>
            <h2
              style={{
                fontSize: "1.8rem",
                fontWeight: "700",
                color: "var(--text-primary)",
                marginBottom: "0.5rem",
                margin: "0",
              }}
            >
              {t('auth.getStartedNow')}
            </h2>
            <p
              style={{
                color: "var(--text-secondary)",
                fontSize: "0.9rem",
                margin: "0.5rem 0 0 0",
              }}
            >
              {t('auth.loginToContinue')}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div
                style={{
                  background: "rgba(239, 68, 68, 0.1)",
                  color: "var(--error-dark)",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  marginBottom: "1rem",
                  fontSize: "0.85rem",
                }}
              >
                {error}
              </div>
            )}

            {/* Email Field */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "0.5rem",
                  color: "var(--text-primary)",
                  fontSize: "0.85rem",
                }}
              >
                {t('auth.emailAddress')}
              </label>
              <input
                type="email"
                name="email"
                placeholder={t('auth.enterYourEmail')}
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Password Field */}
            <div style={{ marginBottom: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <label
                  style={{
                    fontWeight: "500",
                    color: "var(--text-primary)",
                    fontSize: "0.85rem",
                  }}
                >
                  {t('auth.password')}
                </label>
                <Link
                  to="/forgot-password"
                  style={{
                    color: "var(--primary-color)",
                    textDecoration: "none",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                  }}
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t('auth.enterYourPassword')}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    paddingRight: "3rem",
                    border: "1px solid var(--border-color)",
                    borderRadius: "8px",
                    fontSize: "0.9rem",
                    background: "var(--bg-primary)",
                    color: "var(--text-primary)",
                    boxSizing: "border-box",
                  }}
                />
                <div
                  onClick={togglePasswordVisibility}
                  style={{
                    position: "absolute",
                    right: "0.75rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "var(--text-secondary)",
                    display: "flex",
                  }}
                >
                  <Icon
                    icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
                    width="20"
                    height="20"
                  />
                </div>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                width: "100%",
                marginBottom: "1.5rem",
                marginTop: "1.5rem",
              }}
            >
              {loading ? t('auth.loggingIn') : t('auth.logIn')}
            </button>

            {/* Sign up link */}
            <div
              style={{
                textAlign: "center",
                fontSize: "0.85rem",
                marginBottom: "1.5rem",
              }}
            >
              <span style={{ color: "var(--text-secondary)" }}>{t('auth.dontHaveAccount')} </span>
              <Link
                to="/register"
                style={{
                  color: "var(--primary-color)",
                  textDecoration: "none",
                  fontWeight: "600",
                }}
              >
                {t('auth.signUp')}
              </Link>
            </div>

            {/* Divider */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  background: "var(--border-color)",
                }}
              ></div>
              <span
                style={{
                  padding: "0 1rem",
                  color: "var(--text-secondary)",
                  fontSize: "0.85rem",
                }}
              >
                {t('auth.or')}
              </span>
              <div
                style={{
                  flex: 1,
                  height: "1px",
                  background: "var(--border-color)",
                }}
              ></div>
            </div>

            {/* Social Login Buttons */}
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                flexDirection: "column",
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                onClick={handleGoogleLogin}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  width: "100%",
                }}
              >
                <Icon icon="devicon:google" width="18" height="18" />
                {t('auth.loginWithGoogle')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
